import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI, interviewAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  GraduationCap, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Loader2, FileText, BookOpen, Lightbulb, Target, MessageSquare,
  CheckCircle, Play, Award, Users, Briefcase, Star, ArrowRight,
  ExternalLink, Monitor, BarChart3, ShoppingCart, Wrench, Heart,
  Code, Camera, Truck, Search
} from 'lucide-react';
import { toast } from 'sonner';

const TrainingPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [cvs, setCvs] = useState([]);
  const [selectedCV, setSelectedCV] = useState('');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [activeModule, setActiveModule] = useState('free-courses');
  const [courseFilter, setCourseFilter] = useState('all');

  useEffect(() => {
    loadCVs();
  }, []);

  const loadCVs = async () => {
    try {
      const response = await cvAPI.list();
      setCvs(response.data);
      if (response.data.length > 0) {
        setSelectedCV(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load CVs');
    }
  };

  const generateQuestions = async () => {
    if (!selectedCV) {
      toast.error('Please select a CV');
      return;
    }
    setLoading(true);
    try {
      const response = await interviewAPI.generateQuestions(selectedCV);
      setQuestions(response.data);
      toast.success('Questions generated!');
    } catch (error) {
      toast.error('Failed to generate questions');
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (id) => {
    setExpandedQuestions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const trainingModules = [
    {
      id: 'free-courses',
      title: 'Free Courses',
      icon: <Award className="w-5 h-5" />,
      description: 'Get free certificates to boost your CV',
      color: 'bg-violet-500'
    },
    {
      id: 'interview',
      title: 'Interview Prep',
      icon: <MessageSquare className="w-5 h-5" />,
      description: 'Practice answering questions based on your CV',
      color: 'bg-emerald-500'
    },
    {
      id: 'tips',
      title: 'CV Tips',
      icon: <Lightbulb className="w-5 h-5" />,
      description: 'Learn how to improve your CV',
      color: 'bg-amber-500'
    },
    {
      id: 'job-search',
      title: 'Job Search',
      icon: <Target className="w-5 h-5" />,
      description: 'Strategies for finding opportunities',
      color: 'bg-blue-500'
    }
  ];

  const courseCategories = [
    { id: 'all', label: 'All Courses', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'office', label: 'Office Skills', icon: <Monitor className="w-4 h-4" /> },
    { id: 'digital', label: 'Digital Skills', icon: <Code className="w-4 h-4" /> },
    { id: 'business', label: 'Business', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'retail', label: 'Retail & Hospitality', icon: <ShoppingCart className="w-4 h-4" /> },
    { id: 'health', label: 'Health & Safety', icon: <Heart className="w-4 h-4" /> },
    { id: 'trade', label: 'Trades & Technical', icon: <Wrench className="w-4 h-4" /> },
    { id: 'creative', label: 'Creative & Media', icon: <Camera className="w-4 h-4" /> },
    { id: 'logistics', label: 'Logistics & Admin', icon: <Truck className="w-4 h-4" /> },
  ];

  const freeCourses = [
    // Office Skills
    { title: "Microsoft Word Essentials", provider: "Alison", url: "https://alison.com/course/microsoft-word-2019-essentials", category: "office", duration: "3-4 hrs", certificate: true, description: "Learn document formatting, tables, templates, and professional letter writing in Word." },
    { title: "Microsoft Excel for Beginners", provider: "Alison", url: "https://alison.com/course/microsoft-excel-2019-essentials", category: "office", duration: "3-4 hrs", certificate: true, description: "Master spreadsheets, formulas, charts, and data organisation in Excel." },
    { title: "Microsoft PowerPoint Basics", provider: "Alison", url: "https://alison.com/course/microsoft-powerpoint-2019-essentials", category: "office", duration: "2-3 hrs", certificate: true, description: "Create professional presentations with slides, animations, and design." },
    { title: "Microsoft Office Suite (Word, Excel, PowerPoint)", provider: "Alison", url: "https://alison.com/course/microsoft-office-2019-new-features", category: "office", duration: "4-5 hrs", certificate: true, description: "Comprehensive overview of all key Microsoft Office applications." },
    { title: "Google Workspace (Docs, Sheets, Slides)", provider: "Google", url: "https://skillshop.withgoogle.com/", category: "office", duration: "Self-paced", certificate: true, description: "Learn Google Docs, Sheets, Slides, and Drive — free from Google." },
    { title: "Typing Speed & Accuracy", provider: "Alison", url: "https://alison.com/course/touch-typing-training", category: "office", duration: "2-3 hrs", certificate: true, description: "Improve your typing speed for data capture and admin roles." },

    // Digital Skills
    { title: "Fundamentals of Digital Marketing", provider: "Google", url: "https://learndigital.withgoogle.com/digitalgarage/course/digital-marketing", category: "digital", duration: "40 hrs", certificate: true, description: "Google-certified course covering SEO, social media, email marketing, and analytics." },
    { title: "Introduction to Computers", provider: "Alison", url: "https://alison.com/course/introduction-to-computers", category: "digital", duration: "2-3 hrs", certificate: true, description: "Learn basic computer skills — hardware, software, files, and the internet." },
    { title: "Social Media Marketing", provider: "HubSpot", url: "https://academy.hubspot.com/courses/social-media", category: "digital", duration: "5 hrs", certificate: true, description: "Learn to create social media strategies for businesses." },
    { title: "Introduction to Cybersecurity", provider: "Cisco", url: "https://www.netacad.com/courses/cybersecurity/introduction-cybersecurity", category: "digital", duration: "6 hrs", certificate: true, description: "Understand online threats, data privacy, and how to stay safe online." },
    { title: "Basic Computer Skills", provider: "GCFGlobal", url: "https://edu.gcfglobal.org/en/basic-computer-skills/", category: "digital", duration: "Self-paced", certificate: false, description: "Step-by-step free tutorials on using computers, internet, and email." },
    { title: "Email Marketing Certification", provider: "HubSpot", url: "https://academy.hubspot.com/courses/email-marketing", category: "digital", duration: "4 hrs", certificate: true, description: "Learn email campaigns, automation, and analytics for business communication." },
    { title: "Introduction to Data Analytics", provider: "Google", url: "https://www.coursera.org/learn/foundations-data", category: "digital", duration: "20 hrs", certificate: true, description: "Google Career Certificate intro — learn data analysis thinking and tools (free to audit)." },
    { title: "Introduction to Programming with Python", provider: "Harvard (CS50)", url: "https://cs50.harvard.edu/python/", category: "digital", duration: "Self-paced", certificate: true, description: "Harvard's free intro to programming — great for aspiring developers." },

    // Business Skills
    { title: "Project Management Fundamentals", provider: "Alison", url: "https://alison.com/course/project-management-fundamentals", category: "business", duration: "3-4 hrs", certificate: true, description: "Learn planning, scheduling, risk management, and team coordination." },
    { title: "Google Project Management Certificate", provider: "Google", url: "https://www.coursera.org/professional-certificates/google-project-management", category: "business", duration: "Self-paced", certificate: true, description: "Google Career Certificate in project management — free to audit on Coursera." },
    { title: "Communication Skills", provider: "Alison", url: "https://alison.com/course/communication-skills-for-the-workplace", category: "business", duration: "2-3 hrs", certificate: true, description: "Professional communication, email writing, and workplace interaction skills." },
    { title: "Entrepreneurship Essentials", provider: "Alison", url: "https://alison.com/course/entrepreneurship-essentials", category: "business", duration: "3-4 hrs", certificate: true, description: "Starting a business, business plans, marketing, and financial management basics." },
    { title: "Financial Literacy", provider: "Alison", url: "https://alison.com/course/financial-literacy", category: "business", duration: "2-3 hrs", certificate: true, description: "Understand budgeting, savings, debt management, and personal finance." },
    { title: "Human Resources (HR) Basics", provider: "Alison", url: "https://alison.com/course/human-resources", category: "business", duration: "3-4 hrs", certificate: true, description: "Learn recruitment, onboarding, performance management, and labour law basics." },
    { title: "Bookkeeping Basics", provider: "Alison", url: "https://alison.com/course/accounting-and-bookkeeping", category: "business", duration: "3-4 hrs", certificate: true, description: "Learn debits, credits, journals, ledgers, and basic accounting for small business." },
    { title: "Content Marketing Certification", provider: "HubSpot", url: "https://academy.hubspot.com/courses/content-marketing", category: "business", duration: "6 hrs", certificate: true, description: "Learn content strategy, blogging, SEO, and content promotion." },

    // Retail & Hospitality
    { title: "Customer Service Skills", provider: "Alison", url: "https://alison.com/course/customer-service-skills", category: "retail", duration: "2-3 hrs", certificate: true, description: "Handle complaints, communicate effectively, and deliver great service." },
    { title: "Retail Management", provider: "Alison", url: "https://alison.com/course/introduction-to-retail-management", category: "retail", duration: "3-4 hrs", certificate: true, description: "Stock management, visual merchandising, POS systems, and store operations." },
    { title: "Food Safety & Hygiene", provider: "Alison", url: "https://alison.com/course/food-safety-training", category: "retail", duration: "2-3 hrs", certificate: true, description: "Essential for restaurant, catering, and hospitality jobs — learn food handling and hygiene." },
    { title: "Hospitality Management", provider: "Alison", url: "https://alison.com/course/hospitality-management", category: "retail", duration: "3-4 hrs", certificate: true, description: "Hotel operations, front desk, housekeeping, and guest service management." },
    { title: "Sales Skills Training", provider: "Alison", url: "https://alison.com/course/sales-skills-training", category: "retail", duration: "2-3 hrs", certificate: true, description: "Closing techniques, objection handling, and customer relationship building." },

    // Health & Safety
    { title: "Occupational Health & Safety", provider: "Alison", url: "https://alison.com/course/occupational-health-and-safety", category: "health", duration: "3-4 hrs", certificate: true, description: "Workplace safety, hazard identification, and OHS Act compliance — essential for SA workplaces." },
    { title: "First Aid Basics", provider: "Alison", url: "https://alison.com/course/first-aid-basics", category: "health", duration: "2-3 hrs", certificate: true, description: "CPR, wound care, burns, and emergency response basics." },
    { title: "Mental Health Awareness", provider: "Alison", url: "https://alison.com/course/mental-health-awareness", category: "health", duration: "2-3 hrs", certificate: true, description: "Understanding mental health, stress management, and workplace wellbeing." },
    { title: "HIV/AIDS Awareness", provider: "Alison", url: "https://alison.com/course/understanding-hiv-and-aids", category: "health", duration: "2-3 hrs", certificate: true, description: "Important workplace knowledge for South African employees." },
    { title: "Care Worker Training", provider: "Alison", url: "https://alison.com/course/care-support-worker", category: "health", duration: "3-4 hrs", certificate: true, description: "Patient care, elder care, disability support, and care ethics." },

    // Trades & Technical
    { title: "Introduction to Electrical Engineering", provider: "Alison", url: "https://alison.com/course/introduction-to-electrical-engineering", category: "trade", duration: "3-4 hrs", certificate: true, description: "Circuits, wiring basics, and electrical safety — foundation for technical roles." },
    { title: "Construction Safety (OSHA)", provider: "Alison", url: "https://alison.com/course/construction-safety", category: "trade", duration: "2-3 hrs", certificate: true, description: "Site safety, PPE, hazard management for construction workers." },
    { title: "Motor Mechanic Fundamentals", provider: "Alison", url: "https://alison.com/course/motor-mechanic", category: "trade", duration: "3-4 hrs", certificate: true, description: "Engine basics, maintenance, diagnostics, and repair fundamentals." },
    { title: "Plumbing Basics", provider: "Alison", url: "https://alison.com/course/plumbing-basics", category: "trade", duration: "2-3 hrs", certificate: true, description: "Pipes, fittings, water systems, and basic plumbing repair skills." },
    { title: "Carpentry & Woodworking", provider: "Alison", url: "https://alison.com/course/carpentry", category: "trade", duration: "3-4 hrs", certificate: true, description: "Wood types, hand tools, joints, and basic furniture construction." },

    // Creative & Media
    { title: "Graphic Design Basics (Canva)", provider: "Alison", url: "https://alison.com/course/graphic-design", category: "creative", duration: "3-4 hrs", certificate: true, description: "Design principles, colour theory, and creating visuals with free tools." },
    { title: "Photography Basics", provider: "Alison", url: "https://alison.com/course/photography-fundamentals", category: "creative", duration: "2-3 hrs", certificate: true, description: "Composition, lighting, camera settings, and photo editing basics." },
    { title: "Introduction to Video Editing", provider: "Alison", url: "https://alison.com/course/video-production", category: "creative", duration: "3-4 hrs", certificate: true, description: "Learn video editing, transitions, audio, and storytelling for social media." },
    { title: "Copywriting Fundamentals", provider: "Alison", url: "https://alison.com/course/copywriting", category: "creative", duration: "2-3 hrs", certificate: true, description: "Writing persuasive content for ads, websites, and social media." },

    // Logistics & Admin
    { title: "Supply Chain Management", provider: "Alison", url: "https://alison.com/course/supply-chain-management", category: "logistics", duration: "3-4 hrs", certificate: true, description: "Procurement, warehousing, distribution, and inventory management." },
    { title: "Office Administration", provider: "Alison", url: "https://alison.com/course/office-administration", category: "logistics", duration: "3-4 hrs", certificate: true, description: "Filing, scheduling, correspondence, and office management procedures." },
    { title: "Data Entry & Record Keeping", provider: "Alison", url: "https://alison.com/course/data-entry-clerk", category: "logistics", duration: "2-3 hrs", certificate: true, description: "Accurate data capture, spreadsheets, and database entry skills." },
    { title: "Warehouse Operations", provider: "Alison", url: "https://alison.com/course/warehouse-management", category: "logistics", duration: "3-4 hrs", certificate: true, description: "Stock control, receiving, dispatching, and health and safety in warehouses." },
    { title: "Receptionist Skills", provider: "Alison", url: "https://alison.com/course/receptionist-skills", category: "logistics", duration: "2-3 hrs", certificate: true, description: "Phone etiquette, scheduling, visitor management, and front desk operations." },
  ];

  const filteredCourses = courseFilter === 'all' 
    ? freeCourses 
    : freeCourses.filter(c => c.category === courseFilter);

  const cvTips = [
    {
      title: "Keep it to 2 pages maximum",
      description: "Recruiters spend only 6-7 seconds scanning a CV. Keep it concise and relevant to the job you're applying for.",
      icon: <FileText className="w-5 h-5" />
    },
    {
      title: "Use action verbs",
      description: "Start bullet points with strong action verbs like 'Managed', 'Developed', 'Achieved', 'Implemented'.",
      icon: <Play className="w-5 h-5" />
    },
    {
      title: "Quantify achievements",
      description: "Use numbers where possible: 'Increased sales by 25%' is better than 'Improved sales performance'.",
      icon: <Award className="w-5 h-5" />
    },
    {
      title: "Tailor for each job",
      description: "Customize your CV for each application. Highlight skills and experience relevant to the specific role.",
      icon: <Target className="w-5 h-5" />
    },
    {
      title: "Include relevant keywords",
      description: "Many companies use ATS systems. Include keywords from the job description to pass automated screening.",
      icon: <CheckCircle className="w-5 h-5" />
    },
    {
      title: "Proofread carefully",
      description: "Spelling and grammar errors can disqualify you immediately. Have someone else review your CV.",
      icon: <Star className="w-5 h-5" />
    }
  ];

  const jobSearchTips = [
    {
      title: "Use Multiple Job Boards",
      description: "Don't limit yourself to one platform. Try Careers24, Indeed, LinkedIn, PNet, and company websites directly.",
      sites: ["careers24.com", "za.indeed.com", "linkedin.com", "pnet.co.za"]
    },
    {
      title: "Network Actively",
      description: "Many jobs in South Africa are filled through referrals. Tell friends, family, and former colleagues you're job hunting.",
    },
    {
      title: "Follow Up",
      description: "After applying, follow up after 1-2 weeks if you haven't heard back. Shows initiative and interest.",
    },
    {
      title: "Prepare for Interviews",
      description: "Research the company, prepare questions to ask, and practice common interview questions.",
    },
    {
      title: "Consider Learnerships",
      description: "If you lack experience, learnerships and internships can help you gain skills while earning.",
    }
  ];

  const getCategoryColor = (category) => {
    switch (category) {
      case 'General': return 'bg-blue-100 text-blue-700';
      case 'Experience': return 'bg-emerald-100 text-emerald-700';
      case 'Skills': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white px-4 lg:px-8 py-6">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-300 hover:text-white hover:bg-slate-800 mb-4"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold">Career Training Centre</h1>
              <p className="text-slate-400">Prepare for success with our resources</p>
            </div>
          </div>
        </div>
      </header>

      {/* Module Selection */}
      <div className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex gap-3 overflow-x-auto pb-2">
            {trainingModules.map((module) => (
              <button
                key={module.id}
                onClick={() => setActiveModule(module.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap transition-all ${
                  activeModule === module.id
                    ? `${module.color} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {module.icon}
                <span className="font-medium">{module.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="py-8 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          
          {/* Free Courses Module */}
          {activeModule === 'free-courses' && (
            <div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Free Courses & Certificates</h2>
                    <p className="text-slate-600 mt-1">
                      Boost your CV with free online training. Complete a course, get a certificate, and add it to your skills.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 bg-violet-50 text-violet-700 px-3 py-1.5 rounded-full text-sm font-medium shrink-0">
                    <Award className="w-4 h-4" />
                    {freeCourses.filter(c => c.certificate).length} with certificates
                  </div>
                </div>
              </div>

              {/* Category Filter */}
              <div className="flex gap-2 overflow-x-auto pb-3 mb-6 scrollbar-hide">
                {courseCategories.map((cat) => (
                  <button
                    key={cat.id}
                    data-testid={`course-filter-${cat.id}`}
                    onClick={() => setCourseFilter(cat.id)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                      courseFilter === cat.id
                        ? 'bg-slate-900 text-white shadow-md'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {cat.icon}
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Course Count */}
              <p className="text-sm text-slate-500 mb-4">
                Showing {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''}
                {courseFilter !== 'all' && ` in ${courseCategories.find(c => c.id === courseFilter)?.label}`}
              </p>

              {/* Course Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                {filteredCourses.map((course, index) => (
                  <div
                    key={index}
                    onClick={() => window.open(course.url, '_blank', 'noopener,noreferrer')}
                    data-testid={`course-card-${index}`}
                    className="group bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-violet-200 transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3 className="font-semibold text-slate-900 group-hover:text-violet-700 transition-colors">
                        {course.title}
                      </h3>
                      <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-violet-500 shrink-0 mt-0.5 transition-colors" />
                    </div>
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{course.description}</p>
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full font-medium">
                        {course.provider}
                      </span>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                        {course.duration}
                      </span>
                      {course.certificate && (
                        <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                          <Award className="w-3 h-3" />
                          Free Certificate
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Tip Box */}
              <div className="mt-8 bg-violet-50 border border-violet-200 rounded-2xl p-6">
                <h3 className="font-semibold text-violet-900 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  How to use these certificates
                </h3>
                <ul className="text-sm text-violet-800 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
                    Complete the course and download your certificate from the provider
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
                    Add the course name under "Skills" or "Training" on your CV
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
                    Mention certificates in your cover letter to stand out
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
                    Upload certificates to LinkedIn to increase profile visibility
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Interview Prep Module */}
          {activeModule === 'interview' && (
            <div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">Interview Question Generator</h2>
                <p className="text-slate-600 mb-6">
                  Generate personalized interview questions based on your CV. Practice answering these to prepare for real interviews.
                </p>

                {!isSubscribed ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                    <GraduationCap className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                    <h3 className="font-semibold text-amber-900 mb-2">Subscription Required</h3>
                    <p className="text-amber-700 mb-4">Subscribe to generate personalized interview questions</p>
                    <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
                      Subscribe Now
                    </Button>
                  </div>
                ) : cvs.length === 0 ? (
                  <div className="bg-slate-50 rounded-xl p-6 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-semibold text-slate-900 mb-2">No CVs Found</h3>
                    <p className="text-slate-600 mb-4">Create a CV first to generate interview questions</p>
                    <Button onClick={() => navigate('/cv-builder')} className="bg-slate-900 hover:bg-slate-800">
                      Create Your CV
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Select value={selectedCV} onValueChange={setSelectedCV}>
                      <SelectTrigger className="h-12 flex-1">
                        <SelectValue placeholder="Choose a CV" />
                      </SelectTrigger>
                      <SelectContent>
                        {cvs.map((cv) => (
                          <SelectItem key={cv.id} value={cv.id}>
                            {cv.title || cv.personal_details?.full_name || 'Untitled CV'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={generateQuestions}
                      disabled={loading || !selectedCV}
                      className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <BookOpen className="w-5 h-5 mr-2" />
                          Generate Questions
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Questions List */}
              {questions.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Practice Questions ({questions.length})</h3>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setExpandedQuestions(
                        questions.reduce((acc, q) => ({ ...acc, [q.id]: true }), {})
                      )}
                    >
                      Expand All
                    </Button>
                  </div>

                  {questions.map((q, index) => (
                    <div key={q.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                      <button
                        onClick={() => toggleQuestion(q.id)}
                        className="w-full p-5 flex items-start gap-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 font-semibold flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(q.category)}`}>
                              {q.category}
                            </span>
                          </div>
                          <p className="font-medium text-slate-900">{q.question}</p>
                        </div>
                        {expandedQuestions[q.id] ? (
                          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                      
                      {expandedQuestions[q.id] && q.suggested_answer && (
                        <div className="px-5 pb-5 pt-0">
                          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Lightbulb className="w-4 h-4 text-emerald-600" />
                              <span className="text-sm font-semibold text-emerald-700">Suggested Answer Points</span>
                            </div>
                            <p className="text-sm text-emerald-800">{q.suggested_answer}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Common Interview Questions */}
              <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Common Interview Questions</h3>
                <div className="space-y-3">
                  {[
                    "Tell me about yourself.",
                    "Why do you want to work here?",
                    "What are your greatest strengths?",
                    "What is your biggest weakness?",
                    "Where do you see yourself in 5 years?",
                    "Why should we hire you?",
                    "Describe a challenge you've overcome.",
                    "What are your salary expectations?"
                  ].map((question, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium text-slate-600">
                        {i + 1}
                      </span>
                      <span className="text-slate-700">{question}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CV Tips Module */}
          {activeModule === 'tips' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">CV Writing Tips</h2>
                <p className="text-slate-600">Expert advice to make your CV stand out from the crowd.</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {cvTips.map((tip, index) => (
                  <div key={index} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600 flex-shrink-0">
                        {tip.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{tip.title}</h3>
                        <p className="text-sm text-slate-600">{tip.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Pro Tip: Use Our AI Enhancement
                </h3>
                <p className="text-amber-800 mb-4">
                  Our AI can help improve your CV wording without inventing false information. 
                  Try the "Enhance" button when editing your CV to see the magic!
                </p>
                <Button onClick={() => navigate('/cv-builder')} className="bg-amber-500 hover:bg-amber-600">
                  Go to CV Builder
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Job Search Guide Module */}
          {activeModule === 'job-search' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">Job Search Guide for South Africa</h2>
                <p className="text-slate-600">Strategies and resources to help you find your next opportunity.</p>
              </div>

              <div className="space-y-4">
                {jobSearchTips.map((tip, index) => (
                  <div key={index} className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-bold flex-shrink-0">
                        {index + 1}
                      </span>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{tip.title}</h3>
                        <p className="text-slate-600 text-sm mb-2">{tip.description}</p>
                        {tip.sites && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {tip.sites.map((site, i) => (
                              <span key={i} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full">
                                {site}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Popular Job Sites */}
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Popular Job Sites in South Africa
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { name: "Careers24", url: "careers24.com" },
                    { name: "Indeed SA", url: "za.indeed.com" },
                    { name: "LinkedIn", url: "linkedin.com" },
                    { name: "PNet", url: "pnet.co.za" },
                    { name: "JobMail", url: "jobmail.co.za" },
                    { name: "Gumtree Jobs", url: "gumtree.co.za" },
                    { name: "BestJobs", url: "bestjobs.co.za" },
                    { name: "Adzuna", url: "adzuna.co.za" }
                  ].map((site, i) => (
                    <a
                      key={i}
                      href={`https://${site.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white rounded-lg p-3 text-center hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-slate-900">{site.name}</p>
                      <p className="text-xs text-blue-600">{site.url}</p>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TrainingPage;
